import { useState, useEffect, useRef, useCallback } from 'react';
import '../../styles/ml.css';
import mascotImg from '../../img/NOVA-AI-Mascot.png';

const LOCAL_STORAGE_KEY = 'novaai_conversations';
const LEGACY_STORAGE_KEY = 'novaai_chat_messages';

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const migrateFromLegacy = () => {
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!legacy) return null;
    try {
        const messages = JSON.parse(legacy);
        if (Array.isArray(messages) && messages.length > 0) {
            const firstUser = messages.find((m) => m.role === 'user');
            const title = firstUser
                ? firstUser.content.slice(0, 40) + (firstUser.content.length > 40 ? '…' : '')
                : 'Conversación anterior';
            return [
                {
                    id: generateId(),
                    title,
                    messages,
                    createdAt: Date.now(),
                },
            ];
        }
    } catch {
        /* ignore */
    }
    return null;
};

export default function ChatUI() {
    const [conversations, setConversations] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (saved) {
            try {
                const data = JSON.parse(saved);
                if (Array.isArray(data) && data.length > 0) {
                    setConversations(data);
                    setActiveId(data[0].id);
                    return;
                }
            } catch {
                /* ignore */
            }
        }
        const migrated = migrateFromLegacy();
        if (migrated) {
            setConversations(migrated);
            setActiveId(migrated[0].id);
            localStorage.removeItem(LEGACY_STORAGE_KEY);
        }
    }, []);

    useEffect(() => {
        if (conversations.length > 0) {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(conversations));
        } else {
            localStorage.removeItem(LOCAL_STORAGE_KEY);
        }
    }, [conversations]);

    const activeConversation = conversations.find((c) => c.id === activeId);
    const messages = activeConversation?.messages ?? [];

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [activeId, loading, conversations]);

    useEffect(() => {
        if (textareaRef.current && !input) {
            textareaRef.current.style.height = '24px';
        }
    }, [input]);

    const createConversation = useCallback(() => {
        const id = generateId();
        const newConv = {
            id,
            title: 'Nueva conversación',
            messages: [],
            createdAt: Date.now(),
        };
        setConversations((prev) => [newConv, ...prev]);
        setActiveId(id);
    }, []);

    const deleteConversation = useCallback((e, id) => {
        e.stopPropagation();
        setConversations((prev) => {
            const next = prev.filter((c) => c.id !== id);
            setActiveId((current) => {
                if (current === id) {
                    return next.length > 0 ? next[0].id : null;
                }
                return current;
            });
            return next;
        });
    }, []);

    const updateConversation = useCallback((id, updater) => {
        setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, ...updater(c) } : c)));
    }, []);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessage = { id: generateId(), role: 'user', content: input };
        const question = input;
        setInput('');

        let targetId = activeId;
        const isEmptyConv = (activeConversation?.messages?.length ?? 0) === 0;
        const title = question.slice(0, 40) + (question.length > 40 ? '…' : '');

        if (!targetId) {
            targetId = generateId();
            setConversations((prev) => [
                { id: targetId, title, messages: [userMessage], createdAt: Date.now() },
                ...prev,
            ]);
            setActiveId(targetId);
        } else {
            updateConversation(targetId, (c) => ({
                title: isEmptyConv ? title : c.title,
                messages: [...(c.messages || []), userMessage],
            }));
        }

        setLoading(true);
        try {
            const res = await fetch('https://TU_NGROK_URL.ngrok-free.app/ask', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ question }),
                mode: 'cors',
            });
            if (!res.ok) {
                console.error('Error en la respuesta del servidor:', res.status, res.statusText);
                return;
            }
            const data = await res.json();
            const botMessage = { id: generateId(), role: 'bot', content: data.response };
            setConversations((prev) =>
                prev.map((c) =>
                    c.id === targetId ? { ...c, messages: [...(c.messages || []), botMessage] } : c
                )
            );
        } catch (err) {
            console.error('❌ Error al llamar a la API:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleTextareaChange = (e) => {
        setInput(e.target.value);
        const ta = e.target;
        ta.style.height = '24px';
        ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
    };

    return (
        <div className="ml-chat-container">
            <aside className="ml-chat-sidebar">
                <div className="ml-chat-sidebar-header">
                    <button type="button" className="ml-chat-new-btn" onClick={createConversation}>
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Nueva conversación
                    </button>
                </div>
                <div className="ml-chat-conversations-list">
                    {conversations.map((conv) => (
                        <div
                            key={conv.id}
                            role="button"
                            tabIndex={0}
                            className={`ml-chat-conversation-item ${
                                conv.id === activeId ? 'ml-chat-conversation-active' : ''
                            }`}
                            onClick={() => setActiveId(conv.id)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setActiveId(conv.id);
                                }
                            }}
                        >
                            <span className="ml-chat-conversation-title">{conv.title}</span>
                            <button
                                type="button"
                                className="ml-chat-conversation-delete"
                                onClick={(e) => deleteConversation(e, conv.id)}
                                aria-label={`Eliminar ${conv.title}`}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    <line x1="10" y1="11" x2="10" y2="17" />
                                    <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            </aside>

            <div className="ml-chat-main">
                <header className="ml-chat-header">
                    <h1>
                        Chat con <span className="ml-chat-logo-accent">NovaAI</span>
                    </h1>
                </header>

                <div className="ml-chat-messages">
                    <div className="ml-chat-messages-inner">
                        {messages.length === 0 && !loading && (
                            <div className="ml-chat-empty-state">
                                <div className="ml-chat-empty-icon">
                                    <img
                                        src={mascotImg}
                                        alt="NovaAI Mascot"
                                        className="ml-chat-mascot"
                                    />
                                </div>
                                <h2 className="ml-chat-empty-title">¿En qué puedo ayudarte?</h2>
                                <p className="ml-chat-empty-subtitle">
                                    Escribe tu pregunta sobre producción, OEE, paros o cualquier
                                    tema relacionado con NOVA.
                                </p>
                            </div>
                        )}

                        {messages.map((msg) => (
                            <div
                                key={msg.id}
                                className={`ml-chat-message ${
                                    msg.role === 'user'
                                        ? 'ml-chat-message-user'
                                        : 'ml-chat-message-assistant'
                                }`}
                            >
                                <div className="ml-chat-message-avatar">
                                    {msg.role === 'user' ? 'Tú' : 'N'}
                                </div>
                                <div>
                                    <div className="ml-chat-message-label">
                                        {msg.role === 'user' ? 'Tú' : 'NovaAI'}
                                    </div>
                                    <div className="ml-chat-message-content">{msg.content}</div>
                                </div>
                            </div>
                        ))}

                        {loading && (
                            <div className="ml-chat-message ml-chat-message-assistant">
                                <div className="ml-chat-message-avatar">N</div>
                                <div className="ml-chat-loading">
                                    <div className="ml-chat-loading-dots">
                                        <span />
                                        <span />
                                        <span />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>

                <div className="ml-chat-input-area">
                    <div className="ml-chat-input-wrapper">
                        <textarea
                            ref={textareaRef}
                            className="ml-chat-input"
                            placeholder="Escribe tu mensaje..."
                            value={input}
                            onChange={handleTextareaChange}
                            onKeyDown={handleKeyDown}
                            disabled={loading}
                            rows={1}
                        />
                        <button
                            type="button"
                            className="ml-chat-send-btn"
                            onClick={handleSend}
                            disabled={loading || !input.trim()}
                            aria-label="Enviar mensaje"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <line x1="22" y1="2" x2="11" y2="13" />
                                <polygon points="22 2 15 22 11 13 2 9 22 2" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
